import type { EventBus, QualityTier } from '@/contracts';
import type { FrameStats } from './perf';
import { tierRenderScale, type SettingsStore } from './settings';

/**
 * Runtime auto-quality governor (docs/perf-plan.md item 2).
 *
 * `autoDetectTier()` runs once at boot off `deviceMemory`/`hardwareConcurrency`,
 * both of which lie on iGPU laptops. This closes the loop: it reads the
 * always-on frame-time instrument at 1 Hz and walks a quality ladder —
 * renderScale ×1 → ×0.85 → ×0.7 within the tier, then one tier down, and so on
 * to low — whenever wall p95 stays over 20 ms for ~3 s. It climbs back only
 * after a much longer stretch of comfortable frames, with exponential backoff
 * if a climb has to be undone, so it can never oscillate.
 *
 * The player's explicit tier choice is a ceiling: any external
 * `quality:changed` (settings panel, boot auto-detect) resets the ladder top.
 * The governor never promotes past it.
 */

const TIER_LADDER: readonly QualityTier[] = ['low', 'medium', 'high', 'ultra'];
/** renderScale multipliers walked within a tier before dropping tiers. */
const SCALE_STEPS: readonly number[] = [1, 0.85, 0.7];
/** Contract floor for Settings.renderScale. */
const MIN_RENDER_SCALE = 0.5;

const CHECK_INTERVAL_MS = 1000;
/** Demote when p95 exceeds this... */
const SLOW_P95_MS = 20;
/** ...for this many consecutive 1 Hz checks (~3 s sustained). */
const SLOW_STREAK = 3;
/** Promote only when p95 is under this (hysteresis gap vs SLOW_P95_MS)... */
const FAST_P95_MS = 14;
/** ...for this many consecutive checks (~30 s of comfort). */
const FAST_STREAK_BASE = 30;
/** Backoff cap when promotions keep getting undone. */
const FAST_STREAK_MAX = 240;
/** A demotion within this window of a promotion doubles the comfort requirement. */
const RELAPSE_WINDOW_MS = 60_000;
/** Ignore checks briefly after any quality change while the pipeline resettles. */
const SETTLE_MS = 4000;
/** Don't judge a window with fewer frames than this. */
const MIN_SAMPLES = 60;

export interface GovernorState {
  ceiling: QualityTier;
  tier: QualityTier;
  renderScale: number;
  rung: number;
  lastP95: number;
  demotions: number;
  promotions: number;
}

export class QualityGovernor {
  private ceiling: QualityTier;
  private rung = 0;
  private slowStreak = 0;
  private fastStreak = 0;
  private fastStreakRequired = FAST_STREAK_BASE;
  private nextCheck = 0;
  private settleUntil = 0;
  private relapseUntil = 0;
  private selfChange = false;
  private demotions = 0;
  private promotions = 0;
  private lastP95 = 0;
  private readonly offQuality: () => void;
  private readonly stateOut: GovernorState;

  constructor(
    private readonly settings: SettingsStore,
    private readonly perf: FrameStats,
    events: EventBus,
    /** Re-applies pixel ratio / viewport after a renderScale change. */
    private readonly applyViewport: () => void,
  ) {
    this.ceiling = settings.quality;
    this.stateOut = {
      ceiling: this.ceiling, tier: settings.quality, renderScale: settings.renderScale,
      rung: 0, lastP95: 0, demotions: 0, promotions: 0,
    };
    this.offQuality = events.on('quality:changed', ({ tier }) => {
      if (this.selfChange) return;
      // An external change (settings panel / boot auto-detect) is the player's
      // word: it becomes the new ceiling and the ladder restarts from its top.
      this.ceiling = tier;
      this.rung = 0;
      this.slowStreak = 0;
      this.fastStreak = 0;
      this.fastStreakRequired = FAST_STREAK_BASE;
      this.relapseUntil = 0;
      this.settleUntil = performance.now() + SETTLE_MS;
    });
  }

  /** Called once per frame by the engine (skipped while paused). Allocation-free
   *  except the 1 Hz percentile sort inside `perf.snapshot()`. */
  frame(now: number): void {
    if (now < this.nextCheck) return;
    this.nextCheck = now + CHECK_INTERVAL_MS;

    const s = this.perf.snapshot();
    this.lastP95 = s.p95;
    if (s.samples < MIN_SAMPLES || now < this.settleUntil) return;

    if (s.p95 > SLOW_P95_MS) {
      this.fastStreak = 0;
      if (++this.slowStreak >= SLOW_STREAK && this.rung < this.maxRung()) {
        this.slowStreak = 0;
        this.rung++;
        this.demotions++;
        if (now < this.relapseUntil) {
          // A promotion just got undone: demand much longer proof next time.
          this.fastStreakRequired = Math.min(FAST_STREAK_MAX, this.fastStreakRequired * 2);
        }
        this.applyRung(now);
      }
    } else if (s.p95 < FAST_P95_MS) {
      this.slowStreak = 0;
      if (++this.fastStreak >= this.fastStreakRequired && this.rung > 0) {
        this.fastStreak = 0;
        this.rung--;
        this.promotions++;
        this.relapseUntil = now + RELAPSE_WINDOW_MS;
        this.applyRung(now);
      }
    } else {
      this.slowStreak = 0;
      this.fastStreak = 0;
    }
  }

  /** Live state for the perf overlay / console (`game.governorState()`). */
  state(): GovernorState {
    const o = this.stateOut;
    o.ceiling = this.ceiling;
    o.tier = this.settings.quality;
    o.renderScale = this.settings.renderScale;
    o.rung = this.rung;
    o.lastP95 = this.lastP95;
    o.demotions = this.demotions;
    o.promotions = this.promotions;
    return o;
  }

  dispose(): void { this.offQuality(); }

  // --- internals ------------------------------------------------------------

  private maxRung(): number {
    // Three scale steps per tier, from the ceiling down to 'low'.
    return TIER_LADDER.indexOf(this.ceiling) * SCALE_STEPS.length + (SCALE_STEPS.length - 1);
  }

  private applyRung(now: number): void {
    const ceilIdx = TIER_LADDER.indexOf(this.ceiling);
    const tierIdx = Math.max(0, ceilIdx - Math.floor(this.rung / SCALE_STEPS.length));
    const tier = TIER_LADDER[tierIdx];
    const scale = Math.max(
      MIN_RENDER_SCALE,
      tierRenderScale(tier) * SCALE_STEPS[this.rung % SCALE_STEPS.length],
    );

    this.selfChange = true;
    try {
      if (this.settings.quality !== tier) this.settings.set('quality', tier);
      if (this.settings.renderScale !== scale) this.settings.set('renderScale', scale);
    } finally {
      this.selfChange = false;
    }
    this.applyViewport();
    this.settleUntil = now + SETTLE_MS;
    // Judge the new configuration on a fresh window, not the stale slow frames.
    this.perf.reset();
    console.info(`[governor] quality → ${tier} @ renderScale ${scale.toFixed(2)} (rung ${this.rung}/${this.maxRung()}, p95 ${this.lastP95.toFixed(1)} ms)`);
  }
}
